import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Sparkle, MessageSquare, Flame, CalendarClock, Clock, Snowflake, Users, X, Check, ArrowRight,
} from 'lucide-react';
import { autoAlertsAPI } from '../../lib/api';

const TYPE_META = {
    new_inquiry: { Icon: Sparkle, accent: 'text-emerald-400', ring: 'border-emerald-500/40', cta: 'viewBuyer' },
    buyer_replied: { Icon: MessageSquare, accent: 'text-blue-400', ring: 'border-blue-500/40', cta: 'openConversation' },
    high_intent: { Icon: Flame, accent: 'text-amber-400', ring: 'border-amber-500/50', cta: 'viewBuyer' },
    test_drive: { Icon: CalendarClock, accent: 'text-violet-400', ring: 'border-violet-500/40', cta: 'viewRequest' },
    follow_up: { Icon: Clock, accent: 'text-orange-400', ring: 'border-orange-500/40', cta: 'followUp' },
    went_cold: { Icon: Snowflake, accent: 'text-sky-400', ring: 'border-sky-500/40', cta: 'reviewBuyer' },
    vehicle_interest: { Icon: Users, accent: 'text-teal-400', ring: 'border-teal-500/40', cta: 'viewVehicle' },
};

function relTime(iso, t) {
    const d = new Date(iso).getTime();
    if (!d) return '';
    const s = Math.max(0, Math.floor((Date.now() - d) / 1000));
    if (s < 60) return t('autoAlerts.time.justNow');
    const m = Math.floor(s / 60);
    if (m < 60) return t('autoAlerts.time.minAgo', { n: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t('autoAlerts.time.hrAgo', { n: h });
    const days = Math.floor(h / 24);
    return t('autoAlerts.time.dayAgo', { n: days });
}

function describe(a, t) {
    const v = a.vehicle || t('autoAlerts.thisVehicle');
    switch (a.type) {
        case 'new_inquiry': return t('autoAlerts.desc.new_inquiry', { buyer: a.buyer, vehicle: v });
        case 'buyer_replied': return t('autoAlerts.desc.buyer_replied', { buyer: a.buyer, vehicle: v }) + (a.message ? ` “${a.message}”` : '');
        case 'high_intent': return t('autoAlerts.desc.high_intent', { buyer: a.buyer, vehicle: v });
        case 'test_drive': return t('autoAlerts.desc.test_drive', { buyer: a.buyer, vehicle: v });
        case 'follow_up': return t('autoAlerts.desc.follow_up', { buyer: a.buyer, vehicle: v });
        case 'went_cold': return t('autoAlerts.desc.went_cold', { buyer: a.buyer, vehicle: v });
        case 'vehicle_interest': return t('autoAlerts.desc.vehicle_interest', { vehicle: v, count: a.count });
        default: return v;
    }
}

export function AutoSalesAlerts({ organizationId, scope, onOpenConversation, onOpenVehicle }) {
    const { t } = useTranslation();
    const [data, setData] = useState({ alerts: [], unread: 0 });
    const [loading, setLoading] = useState(true);
    // A buyer quotes their ticket — the manager finds the inquiry instantly.
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        if (!organizationId) return;
        setLoading(true);
        try {
            const r = await autoAlertsAPI.list(organizationId, scope);
            setData(r.data || { alerts: [], unread: 0 });
        } catch { setData({ alerts: [], unread: 0 }); }
        setLoading(false);
    }, [organizationId, scope]);
    useEffect(() => { load(); }, [load]);

    const markRead = async (key) => {
        setData((d) => ({ ...d, alerts: d.alerts.map((a) => a.key === key ? { ...a, read: true } : a), unread: Math.max(0, d.unread - 1) }));
        try { await autoAlertsAPI.markRead(organizationId, scope, [key]); } catch { /* noop */ }
    };
    const dismiss = async (key) => {
        setData((d) => ({ ...d, alerts: d.alerts.filter((a) => a.key !== key) }));
        try { await autoAlertsAPI.dismiss(organizationId, scope, key); } catch { /* noop */ }
    };
    const markAll = async () => {
        setData((d) => ({ ...d, alerts: d.alerts.map((a) => ({ ...a, read: true })), unread: 0 }));
        try { await autoAlertsAPI.markRead(organizationId, scope, null, true); } catch { /* noop */ }
    };
    const act = (a) => {
        markRead(a.key);
        if ((a.type === 'buyer_replied' || a.type === 'follow_up' || a.type === 'went_cold' || a.type === 'high_intent' || a.type === 'new_inquiry') && a.request_id && onOpenConversation) onOpenConversation(a.request_id);
        else if (a.vehicle_id && onOpenVehicle) onOpenVehicle(a.vehicle_id);
    };

    return (
        <div className="space-y-3" data-testid="auto-sales-alerts">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400" data-testid="auto-alerts-unread">
                    {t('autoAlerts.unreadCount', { n: data.unread })}
                </p>
                {data.alerts.some((a) => !a.read) && (
                    <button onClick={markAll} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="auto-alerts-mark-all">
                        <Check className="w-3.5 h-3.5" />{t('autoAlerts.markAllRead')}
                    </button>
                )}
            </div>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={t('autoAlerts.searchPlaceholder', 'Search ticket, buyer or vehicle')}
                data-testid="auto-alerts-search"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-400/50 focus:outline-none" />
            {loading ? (
                <p className="text-sm text-slate-500" data-testid="auto-alerts-loading">{t('autoAlerts.loading')}</p>
            ) : data.alerts.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="auto-alerts-empty">{t('autoAlerts.empty')}</p>
            ) : (
                <div className="space-y-2">
                    {data.alerts.filter((a) => {
                        const n = search.trim().toUpperCase();
                        return !n || [a.ticket_code, a.buyer, a.vehicle, a.stock]
                            .filter(Boolean).some((f) => String(f).toUpperCase().includes(n));
                    }).map((a) => {
                        const m = TYPE_META[a.type] || TYPE_META.new_inquiry;
                        const Icon = m.Icon;
                        return (
                            <div key={a.key} data-testid={`auto-alert-${a.type}`}
                                data-read={a.read ? 'true' : 'false'}
                                className={`rounded-xl border ${m.ring} ${a.read ? 'bg-slate-800/30 opacity-80' : 'bg-slate-800/70'} p-3 flex items-start gap-3`}>
                                {!a.read && <span className="mt-1 h-2 w-2 rounded-full bg-amber-400 shrink-0" data-testid="auto-alert-unread-dot" />}
                                <Icon className={`w-5 h-5 shrink-0 ${m.accent} ${a.read ? 'mt-1' : ''}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-[11px] font-bold uppercase tracking-wide ${m.accent}`}>{t(`autoAlerts.type.${a.type}`)}</span>
                                        <span className="text-[11px] text-slate-500">· {relTime(a.occurred_at, t)}</span>
                                        {a.returning_buyer && (
                                            <span className="rounded-md border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-sky-300"
                                                data-testid={`auto-alert-returning-${a.key}`}>
                                                {t('autoAlerts.returningBuyer')}
                                            </span>
                                        )}
                                        {a.ticket_code && (
                                            <span className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-emerald-300"
                                                data-testid={`auto-alert-ticket-${a.key}`}>{a.ticket_code}</span>
                                        )}
                                        {a.claim_reopened && a.claim_reopened_from && (
                                            <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-amber-300"
                                                data-testid={`auto-alert-reopened-${a.key}`}>
                                                {t('autoAlerts.claimReopened', { name: a.claim_reopened_from })}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-slate-100 mt-0.5">{describe(a, t)}</p>
                                    {a.type === 'test_drive' && a.td_date && (
                                        <p className="text-xs text-slate-400 mt-0.5">{a.td_date} · {a.td_time}</p>
                                    )}
                                    <button onClick={() => act(a)} data-testid={`auto-alert-cta-${a.type}`}
                                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-400 hover:text-amber-300">
                                        {t(`autoAlerts.cta.${m.cta}`)}<ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <button onClick={() => dismiss(a.key)} data-testid={`auto-alert-dismiss`}
                                    className="text-slate-500 hover:text-slate-300 shrink-0" title={t('autoAlerts.dismiss')} aria-label={t('autoAlerts.dismiss')}>
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
